import { useUploadTip } from './useModal/modals/uploadTip'
import { useUploadDrag } from './useModal/modals/upload'
import { useActionIntercept } from './useActionIntercept'
import { FILE_TYPE_MAP, RESOURCE_TYPE } from '~/utils/constant'
import { getCopyrightConfirmation } from '~/api'
import DataSource from '@/features/oss-upload/data-source'
import XOssService from '@/features/oss-upload/service'
import { checkFileType } from '~/utils'

export interface IFile {
  uid: string
  upload_secret_id?: string
  percent?: number
  upload_status: number
  name: string
  size: number
  file?: File
  upload_status_query?: boolean
}

export enum ACCEPT {
  IMAGE = '.png,.jpg,.jpeg,.gif',
  VIDEO = '.mp4,.mov,.webm,.mkv',
  AUDIO = '.mp3,.wav',
  PPT = '.ppt,.pptx',
  PDF = '.pdf',
  FONT = '.ttf,.woff,.otf',
}

export const MB_ATOM = Math.pow(1024, 2)

export const FILE_SIZE = {
  IMAGE: 'image_limit',
  VIDEO: 'video_limit',
  AUDIO: 'audio_limit',
  PPT: 'ppt_limit', // 50
  PDF: 'pdf_limit',
  FONT: 'font_limit',
}

interface TIP {
  label: string
  limit: string
  accept: string
}

interface DataType {
  model_type?: typeof COLLECTION_MAP
  resource_type?: RESOURCE_TYPE
  media_type?: FILE_TYPE_MAP
  source?: any
  upload_type?: any
  [key: string]: any
}

export const RESOURCE_TIPS: () => Record<string | number, TIP[]> = () => {
  const { $t } = useNuxtApp()
  return {
    [RESOURCE_TYPE.MATERIAL]: [
      { label: $t('tu-pian'), limit: FILE_SIZE.IMAGE, accept: ACCEPT.IMAGE },
      { label: $t('shi-pin'), limit: FILE_SIZE.VIDEO, accept: ACCEPT.VIDEO },
      { label: 'PPT', limit: FILE_SIZE.PPT, accept: ACCEPT.PPT },
      { label: 'PDF', limit: FILE_SIZE.PDF, accept: ACCEPT.PDF },
    ],
    [RESOURCE_TYPE.MUSIC]: [
      { label: $t('yin-le'), limit: FILE_SIZE.AUDIO, accept: ACCEPT.AUDIO },
    ],
    [RESOURCE_TYPE.SOUND_EFFECT]: [
      { label: $t('yin-xiao'), limit: FILE_SIZE.AUDIO, accept: ACCEPT.AUDIO },
    ],
    [RESOURCE_TYPE.PASTER]: [
      { label: $t('tu-pian'), limit: FILE_SIZE.IMAGE, accept: ACCEPT.IMAGE },
    ],
    [RESOURCE_TYPE.FONT]: [
      { label: $t('zi-ti'), limit: FILE_SIZE.FONT, accept: ACCEPT.FONT },
    ],
    [RESOURCE_TYPE.HEAD]: [
      { label: $t('shi-pin'), limit: FILE_SIZE.VIDEO, accept: ACCEPT.VIDEO },
    ],
    [RESOURCE_TYPE.END]: [
      { label: $t('shi-pin'), limit: FILE_SIZE.VIDEO, accept: ACCEPT.VIDEO },
    ],
  }
}

export const transSize: (size: number, retain?: number) => number = (
  size: number,
  retain = 2
) => {
  return parseFloat((size / MB_ATOM).toFixed(retain))
}

export const getSetingKey = (suffix: string) => {
  const types = ACCEPT as any
  let key
  for (const k in types) {
    if (types[k].includes(suffix)) {
      key = k
      break
    }
  }
  return key
}

interface UploadOptions {
  title?: string
  accept?: string
  params?: DataType
  multiple?: boolean
  hasMessage?: boolean
  noModal?: boolean
  checkSize?: boolean
  beforeUpload?: (file: IFile, list?: FileList) => boolean
  onError?: (file: IFile) => void
  onChange?: (file: IFile) => void
  onSuccess?: (files: IFile, data: any) => void
  onProgress?: (files: IFile) => void
}

export const useUpload = (options: UploadOptions) => {
  let uploadRef: HTMLInputElement

  const { $t } = useNuxtApp()
  const loginInfo = useUserInfo().value
  if (!loginInfo) {
    return {}
  }

  // const userRights = getCookie(COOKIE_KEY.USER_RIGHTS) as any
  const loading = ref(false)
  let dragModal: any = null

  function createInputEl() {
    if (uploadRef) {
      return
    }

    uploadRef = document.createElement('input')
    uploadRef.type = 'file'
    uploadRef.multiple = options.multiple || false
    uploadRef.style.display = 'none'
    uploadRef.onchange = () => onFileChange()
  }

  const beforeUpload = async (file: IFile, list: FileList) => {
    const { $t } = useNuxtApp()

    if (options.beforeUpload) {
      return options.beforeUpload(file, list)
    }

    const { accept } = options
    if (accept !== '*') {
      const acceptList = accept?.split(',') || []
      const checkList = acceptList.filter((item) => {
        if (item.startsWith('.')) {
          return file?.file?.name.toLowerCase().endsWith(item)
        } else {
          return file?.file?.type.startsWith(item.split('/')[0])
        }
      })
      if (checkList.length === 0) {
        message.error(
          $t('qing-shang-chuan-acceptjoin-ge-shi-wen-jian', [accept])
        )
        return false
      }
    }
    if (file.file && file.file.type.includes('image')) {
      const isImgMatch = await checkFileType(file.file)
      if (!isImgMatch) {
        message.error(
          h('span', null, [
            h('a', { href: 'javascript:;', class: 'text-info-1' }, file.name),
            $t('wen-jian-hou-zhui-yu-shi-ji-ge-shi-bu-fu-shang-chuan-shi-bai'),
          ])
        )
        return false
      }
    }

    if (options.checkSize) {
      const suffix = (file?.file?.name.split('.').pop() || '').toLowerCase()
      const key = getSetingKey(suffix)
      if (key && file?.file) {
        const settingSize = loginInfo.rights_record?.[FILE_SIZE[key]]
        if (file.file.size > settingSize) {
          onError(
            file,
            $t(
              'suffixtouppercase-wen-jian-da-xiao-bu-neng-chao-guo-transsize-settingsize-mb-dang-qian-transsizefilesizemb',
              [
                suffix.toUpperCase(),
                transSize(settingSize),
                transSize(file.size),
              ]
            )
          )
          return false
        }
      }
    }

    return true
  }

  const onFileChange = async (files?: FileList) => {
    const list = files || uploadRef.files
    if (!list || list.length === 0) {
      return
    }

    for (const file of list) {
      const suffix = (file.name.split('.').pop() || '').toLowerCase()
      const key = getSetingKey(suffix)
      const rawFile: IFile = {
        uid: uuid(),
        file,
        name: file.name,
        size: file.size,
        upload_status: UPLOAD_STATUS.NO_START,
        percent: 0,
      }
      const check = await beforeUpload(rawFile, list)
      if (check) {
        upload(rawFile)
      }

      if (!options.multiple && list.length > 1) {
        break
      }
    }
    uploadRef!.value = ''
  }

  const upload = async (file: IFile | any) => {
    if (dragModal) {
      dragModal?.destroy?.()
      dragModal = null
    }
    if (options.onChange) {
      options.onChange(file)
    }

    const xossService: any = new XOssService({
      ignore_query_status: true,
      auth: {
        user_id: loginInfo?.user_id,
      },
      dataSource: DataSource,
      params: options.params as any,
      onProgress: (percent: number) => {
        file.upload_secret_id = xossService.upload_secret_id
        file.percent = percent
        file.upload_status = UPLOAD_STATUS.UPLOADING
        options?.onProgress?.(file)
      },
      onFail: (msg: string, extras: XOSS.IExtras) => {
        onError(file, msg)
        if (
          extras?.info?.hint &&
          [
            HINT_MAP.CONTACT_THE_ADMINISTRATOR,
            HINT_MAP.UPGRADE_PERSON,
            HINT_MAP.BUY_STORAGE,
          ].includes(extras?.info?.hint) &&
          extras?.info?.reason === 5
        ) {
          useActionIntercept({ type: extras.info.hint })
        }
      },
      onSuccess: (res: any) => {
        useUpdateUser()
        return onSuccess(file, res)
      },
    })

    loading.value = true

    await xossService.upload(file.file)
  }

  const simpleUpload = async (file: IFile | any) => {
    if (dragModal) {
      dragModal?.destroy?.()
      dragModal = null
    }
    if (options.onChange) {
      options.onChange(file)
    }

    const xossService: any = new XOssService({
      auth: {
        user_id: loginInfo?.user_id,
      },
      dataSource: DataSource,
      params: options.params as any,
      onProgress: (percent: number) => {
        file.percent = percent
        file.upload_status = UPLOAD_STATUS.UPLOADING
        options?.onProgress?.(file)
      },
      onFail: (msg: string) => {
        onError(file, msg)
      },
      onSuccess: (res: any) => {
        return onSuccess(file, res)
      },
    })
    loading.value = true
    await xossService.simpleUpload(file.file)
  }

  const onSuccess = (file: IFile, data: any) => {
    loading.value = false
    file.percent = 100
    file.upload_status = UPLOAD_STATUS.FINISHED
    if (options.hasMessage !== false) {
      message.destroy()
      message.success($t('shang-chuan-cheng-gong'))
    }
    options.onSuccess?.(file, data)
  }

  const onError = (file: IFile, msg?: string) => {
    if (dragModal) {
      dragModal?.destroy?.()
      dragModal = null
    }
    loading.value = false
    file.percent = 100
    file.upload_status = UPLOAD_STATUS.ERROR
    if (options.hasMessage !== false) {
      message.destroy()
      message.error(msg || $t('shang-chuan-shi-bai'))
    }
    options.onError?.(file)
  }

  const _open = () => {
    if (options.noModal) {
      uploadRef.click()
      return
    }
    dragModal = useUploadDrag({
      title: $t('shang-chuan') + (options?.title || $t('wen-jian')),
      resourceType: options?.params?.resource_type,
      onOpen: () => uploadRef.click(),
      onFileChange,
    })
  }

  const open = async (changeOptions?: UploadOptions) => {
    createInputEl()
    Object.assign(options, changeOptions)
    uploadRef.accept = options.accept || ''
    const hasTip = [
      RESOURCE_TYPE.MATERIAL,
      RESOURCE_TYPE.FONT,
      RESOURCE_TYPE.MUSIC,
      RESOURCE_TYPE.SOUND_EFFECT,
    ].includes(options?.params?.resource_type as RESOURCE_TYPE)
    if (hasTip) {
      const confirmationRes = await getCopyrightConfirmation({
        upload_type: options?.params?.upload_type,
        resource_type: options?.params?.resource_type,
      })
      if (confirmationRes.code === 0 && !confirmationRes.data.had_read) {
        useUploadTip(() => {
          _open()
        }, options?.params)
      } else {
        _open()
      }
    } else {
      _open()
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e?.dataTransfer?.files
    if (files) {
      onFileChange(files)
    }
  }

  return { open, loading, onDrop, upload, simpleUpload, onFileChange }
}
